import React, { useMemo } from 'react';
import { InputBase, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { formatHours } from './format';
import { dayStatus, STATUS_STYLE } from './completeness';
import { cellBase, FIRST_COL, stickyLeft } from './tableStyles';
import MonthGrid, { measureDayColumns, measureRightColumn, rightSx } from './MonthGrid';
import { hoursToMinutes } from '../../utils/forecastService';

const NAME_COL = FIRST_COL + 40;
const INPUT_COL = 44;
const FORECAST = 'var(--rt-forecast)';
const EDITABLE_BG = 'var(--rt-forecast-cell)';
const ABSENCE_TINT = { full: 'rgba(229,57,53,0.06)', partial: 'rgba(249,168,37,0.08)' };
const RIGHT_COLUMNS = [
  { key: 'missing', label: 'Missing', color: 'var(--rt-danger)' },
  { key: 'total', label: 'Tot (h)' },
];
export const draftKey = (projectId, day) => `${projectId}|${day}`;

function totalContent(s) {
  if (s.status === 'absence' && !s.minutes) return <span style={{ fontSize: 10 }}>OFF</span>;
  if (s.status === 'missing') return '0';
  return s.minutes ? formatHours(s.minutes) : '';
}

/**
 * Mese dell'utente: una riga per progetto, giorni in colonna, totale del giorno con lo stato di completezza
 * (stesse regole della vista People Manager). In pianificazione i giorni futuri diventano campi forecast (ore).
 */
export default function MyMonthTable({ monthDays, yearMonth, rows, absences, loading, planning, drafts, baseline, onDraftChange, onOpenDay }) {
  // Minuti mostrati in una cella: consuntivo nel passato, forecast nel futuro (bozza in pianificazione)
  const minutesFor = (row, d) => {
    const c = row.days[d.day];
    if (!d.future) return c?.actual || 0;
    if (planning) {
      const draft = drafts[draftKey(row.id, d.day)];
      if (draft !== undefined) return hoursToMinutes(draft);
    }
    return c?.forecast || 0;
  };

  const dayTotals = monthDays.map((d) => rows.reduce((sum, r) => sum + minutesFor(r, d), 0));
  const rowTotals = rows.map((r) => monthDays.reduce((sum, d) => sum + minutesFor(r, d), 0));
  const statuses = monthDays.map((d, i) => dayStatus({ actual: d.future ? 0 : dayTotals[i], forecast: d.future ? dayTotals[i] : 0 }, absences?.get(d.day), d));
  const missing = statuses.reduce((sum, s) => sum + s.missing, 0);
  const grandTotal = rowTotals.reduce((a, b) => a + b, 0);

  // Colonne giorno larghe quanto il valore più lungo (più larghe per i campi in pianificazione)
  const dayWidths = useMemo(
    () =>
      measureDayColumns(
        monthDays,
        (consider) => {
          monthDays.forEach((d, i) => {
            rows.forEach((r) => consider(d.day, minutesFor(r, d) ? formatHours(minutesFor(r, d)) : ''));
            consider(d.day, dayTotals[i] ? formatHours(dayTotals[i]) : '');
          });
        },
        (d) => (planning && d.future ? INPUT_COL : 0),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monthDays, rows, planning, drafts],
  );
  const rightWidth = measureRightColumn(formatHours(grandTotal));
  const dateKey = (d) => `${yearMonth}-${String(d.day).padStart(2, '0')}`;

  const totalRow = {
    cell: (d, i) => {
      const s = statuses[i];
      const style = STATUS_STYLE[s.status];
      const over = !d.future && dayTotals[i] > 480;
      let title = style.title || '';
      if (s.status === 'under' || s.status === 'missing') title = `${formatHours(s.minutes)}h of ${formatHours(s.target)}h`;
      if (over) title = `${formatHours(dayTotals[i])}h: more than 8 hours`;
      if (s.status === 'future' && s.minutes) title = 'Forecast';
      return {
        content: totalContent(s),
        title,
        sx: { ...(style.bg ? { bgcolor: style.bg } : {}), color: over ? 'var(--rt-warn)' : style.color, fontStyle: d.future ? 'italic' : 'normal' },
      };
    },
    right: [
      { content: missing ? formatHours(missing) : '—', sx: { color: missing ? 'var(--rt-danger)' : 'text.disabled' } },
      { content: formatHours(grandTotal) },
    ],
  };

  return (
    <MonthGrid
      monthDays={monthDays}
      firstColLabel="Project"
      firstColWidth={NAME_COL}
      dayWidths={dayWidths}
      rightColumns={RIGHT_COLUMNS}
      rightWidth={rightWidth}
      loading={loading}
      totalRow={rows.length ? totalRow : null}
      fillDeps={[rows.length > 0, planning]}
      // Se la tabella scorre in orizzontale: oggi in vista, o i giorni pianificabili in pianificazione
      focusDayIndex={monthDays.findIndex((d) => (planning ? d.future : d.today))}
      dayHeaderExtra={(d) => {
        const absence = absences?.get(d.day);
        return (
          <Tooltip title={absence ? (absence.full_day ? 'Full-day absence' : 'Partial absence') : ''}>
            <div style={{ fontSize: 9.5, fontWeight: 700, minHeight: 13, color: absence?.full_day ? 'var(--rt-danger)' : 'var(--rt-amber-3)' }}>{absence ? (absence.full_day ? 'OFF' : '½') : ''}</div>
          </Tooltip>
        );
      }}
    >
      {rows.map((r, ri) => (
        <TableRow key={r.id} hover>
          <TableCell sx={{ ...cellBase, ...stickyLeft, textAlign: 'left', px: 2 }}>
            <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }} title={r.name}>
              {r.name}
            </Typography>
            {r.customer && (
              <Typography noWrap sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.2 }}>
                {r.customer}
              </Typography>
            )}
          </TableCell>
          {monthDays.map((d) => {
            const absence = absences?.get(d.day);
            const tint = absence ? ABSENCE_TINT[absence.full_day ? 'full' : 'partial'] : d.weekend ? 'var(--rt-weekend)' : undefined;
            const shadow = d.today ? 'inset 0 0 0 1px var(--rt-today-outline)' : 'none';
            if (planning && d.future) {
              const key = draftKey(r.id, d.day);
              const dirty = (drafts[key] ?? '') !== (baseline[key] ?? '');
              return (
                <TableCell key={d.day} sx={{ ...cellBase, p: 0.25, bgcolor: tint || EDITABLE_BG, boxShadow: shadow }}>
                  <InputBase
                    value={drafts[key] ?? ''}
                    onChange={(e) => onDraftChange(r.id, d.day, e.target.value.replace(/[^0-9.,]/g, ''))}
                    placeholder="·"
                    inputProps={{ 'aria-label': `Forecast hours for ${r.name} on ${dateKey(d)}`, inputMode: 'decimal' }}
                    sx={{
                      width: '100%',
                      height: 26,
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: FORECAST,
                      borderRadius: 1,
                      border: `1px solid ${dirty ? FORECAST : 'var(--rt-hairline-2)'}`,
                      bgcolor: dirty ? 'var(--rt-forecast-soft)' : 'var(--rt-paper)',
                      '& input': { textAlign: 'center', p: 0 },
                      '& input::placeholder': { color: 'var(--rt-placeholder)', opacity: 1 },
                      '&.Mui-focused': { borderColor: FORECAST },
                    }}
                  />
                </TableCell>
              );
            }
            const minutes = minutesFor(r, d);
            return (
              <TableCell
                key={d.day}
                onClick={() => onOpenDay(dateKey(d))}
                title={d.future && minutes ? 'Forecast' : undefined}
                sx={{
                  ...cellBase,
                  cursor: 'pointer',
                  bgcolor: tint || 'background.paper',
                  color: d.future ? FORECAST : 'text.primary',
                  fontStyle: d.future ? 'italic' : 'normal',
                  boxShadow: shadow,
                  '&:hover': { bgcolor: 'rgba(90,176,49,0.14)' },
                }}
              >
                {minutes ? formatHours(minutes) : ''}
              </TableCell>
            );
          })}
          <TableCell sx={{ ...cellBase, ...rightSx(0, 2, rightWidth) }} />
          <TableCell sx={{ ...cellBase, ...rightSx(1, 2, rightWidth), fontWeight: 700 }}>{rowTotals[ri] ? formatHours(rowTotals[ri]) : '—'}</TableCell>
        </TableRow>
      ))}
    </MonthGrid>
  );
}
